import { Component, OnDestroy, OnInit, computed, inject, signal } from '@angular/core';
import { DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { Subscription } from 'rxjs';
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { MessageModule } from 'primeng/message';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { TooltipModule } from 'primeng/tooltip';
import { DialogModule } from 'primeng/dialog';
import { MiddlewareApi } from '../../core/services/middleware-api.service';
import { PermissionsService } from '../../core/services/permissions.service';
import { OrderHandoff, OrderHandoffBreaker, OrderHandoffFailure, StalledHandoff, StalledHandoffs, StalledSendResult } from '../../core/models';
import { orderLabel, orderTooltip } from '../../core/order-vocabulary';
import { relativeTime } from '../edges/store-edge-status';

// The same beat as the watchdog that probes and releases on the server: a breaker that closes, or a probe that goes
// out, shows here on the next tick rather than when somebody presses refresh.
const REFRESH_MS = 15_000;

// Changing the method and resetting a breaker redirect or release orders for EVERY merchant, so they are admin-only
// (consumers:manage), not recipients:manage - which the merchant role holds for its own per-order levers.
const MANAGE = 'consumers:manage';

const RECIPIENT_NAMES: Record<string, string> = { edge: 'Store Edge', xt: 'XT POS (legacy)' };

/** How a bulk send's answer is matched back to a listed row: the send is addressed by location and order id. */
function resultKey(locationId: string, orderId: string): string {
    return `${locationId}|${orderId}`;
}

// WHERE ONLINE ORDERS GO, AND WHETHER THEY ARE GETTING THERE. Three things on one page because they are one question
// on the morning something is wrong: which till are we sending to, is that till answering, and which orders have not
// arrived. The method used to be an environment variable (XT:LegacyHandoffEnabled) and changing it was a redeploy;
// now it is the switch at the top. The breakers never change it by themselves - they hold, probe and release.
@Component({
    selector: 'app-order-handoff',
    standalone: true,
    imports: [FormsModule, RouterLink, DatePipe, CardModule, ButtonModule, TableModule, TagModule, MessageModule, ToggleSwitchModule, TooltipModule, DialogModule],
    template: `
        @if (error()) {
            <!-- Beside the last good read, never instead of it: a failed refresh says nothing about the orders. -->
            <p-message severity="error" styleClass="mb-3">{{ error() }}</p-message>
        }
        @if (note()) {
            <p-message severity="success" styleClass="mb-3">{{ note() }}</p-message>
        }

        <!-- THE OFFER. A switch moves nothing already moving, and an order whose hand-off gave up is moved by nothing
             at all: right after the switch is when somebody wants to know about those, and send them. -->
        @if (offer(); as o) {
            <div class="mb-4 p-3 rounded border border-orange-300 bg-orange-50 dark:bg-orange-950 dark:border-orange-800 flex flex-wrap items-center gap-3">
                <i class="pi pi-inbox text-orange-600"></i>
                <span class="text-sm flex-1 min-w-0">
                    <b>{{ o.count }}</b> {{ o.count === 1 ? 'order was' : 'orders were' }} waiting on {{ name(o.from) }}.
                    Send {{ o.count === 1 ? 'it' : 'them' }} to {{ name(o.to) }}?
                </span>
                <p-button label="Review and send" icon="pi pi-arrow-down" size="small" (onClick)="review()" />
                <p-button label="Not now" size="small" severity="secondary" [text]="true" (onClick)="offer.set(null)" />
            </div>
        }

        <p-card styleClass="mb-4">
            <ng-template pTemplate="title">
                <span>Order hand-off</span>
                <i class="pi pi-info-circle text-surface-400 ml-2 cursor-help"
                   pTooltip="Which system every location's online orders are sent to. Orders already on their way are not moved: each waiting hand-off follows the new choice the next time it is touched, and orders XT already has can still be completed there."
                   tooltipPosition="top"></i>
            </ng-template>

            @if (state(); as s) {
                <!-- ONE SWITCH PER SYSTEM, exactly one on - the same switches the rest of the admin uses. Turning one
                     on (or the live one off) is a request, not the change: the dialog asks why, and Cancel puts the
                     switch back. The switches follow choice(), which only differs from the method while that dialog
                     is open, so a cancel or a failed apply snaps them back to what is actually in force. -->
                <div class="flex flex-col gap-2 mb-3">
                    @for (m of methods; track m.value) {
                        <div class="flex items-center gap-3">
                            <p-toggleswitch [inputId]="'method-' + m.value" [ngModel]="choice() === m.value"
                                            [disabled]="!canManage() || applying()"
                                            (onChange)="switchMethod(m.value, $event.checked)" />
                            <label [for]="'method-' + m.value" class="text-sm font-medium cursor-pointer">{{ m.label }}</label>
                            @if (s.method === m.value) {
                                <p-tag value="receives online orders" [severity]="m.value === 'xt' ? 'warn' : 'info'" />
                            }
                        </div>
                    }
                </div>

                <p class="text-sm text-surface-600 dark:text-surface-300 mb-3">
                    @if (s.source === 'setting') {
                        Set to <b>{{ name(s.method) }}</b>
                        @if (s.changedBy) { by {{ s.changedBy }} }
                        @if (s.changedAt) { <span [attr.title]="s.changedAt | date: 'medium'">{{ since(s.changedAt) }}</span> }
                        @if (s.reason) { — <i>{{ s.reason }}</i> }
                    } @else {
                        Nobody has chosen, so the deployment's default applies: <b>{{ name(s.configuredDefault) }}</b>
                        <span class="text-surface-500">(XT:LegacyHandoffEnabled)</span>.
                    }
                </p>

                <p-dialog [header]="'Send online orders to the ' + name(choice())" [visible]="changing()"
                          (visibleChange)="$event || cancelChange()" [modal]="true" [style]="{ width: '34rem' }" [draggable]="false">
                    <p class="text-sm mb-3">
                        @if (choice() === 'xt') {
                            Every location's new online orders will be <b>registered in XT</b> instead of sent to its
                            store Edge. Orders waiting for the store stay with the store.
                        } @else {
                            Every location's new online orders will go to its <b>store Edge</b>. Orders waiting for XT
                            are moved to the store the next time they are touched - except ones XT may already have,
                            which are left for an operator to check.
                        }
                        Every instance picks this up within a few seconds.
                    </p>
                    <textarea rows="2" [ngModel]="reason()" (ngModelChange)="reason.set($event)"
                              class="w-full text-sm p-2 border border-surface-300 dark:border-surface-700 rounded bg-surface-0 dark:bg-surface-900"
                              placeholder="Why (required - shown beside the method to the next person who looks)"></textarea>
                    <ng-template pTemplate="footer">
                        <p-button label="Cancel" severity="secondary" [text]="true" [disabled]="applying()" (onClick)="cancelChange()" />
                        <p-button label="Apply" icon="pi pi-check" [severity]="choice() === 'xt' ? 'warn' : 'primary'"
                                  [loading]="applying()" [disabled]="!reason()?.trim()" (onClick)="apply()" />
                    </ng-template>
                </p-dialog>
                @if (!canManage()) {
                    <p class="text-xs text-surface-500">Changing this needs the {{ manage }} permission.</p>
                }
            } @else if (!error()) {
                <p class="text-sm text-surface-500">Loading…</p>
            }
        </p-card>

        <!-- One card per recipient. Closed is quiet on purpose; open is loud, because it means orders are waiting. -->
        @if (state(); as s) {
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                @for (b of s.breakers; track b.recipient) {
                    <p-card>
                        <div class="flex items-center gap-2 mb-3">
                            <span class="font-semibold">{{ name(b.recipient) }}</span>
                            <p-tag [value]="stateLabel(b)" [severity]="stateSeverity(b)" [pTooltip]="stateTitle(b)" />
                            @if (b.recipient === s.method) { <p-tag value="receives orders" severity="info" /> }
                            <span class="flex-1"></span>
                            @if (b.state !== 'Closed' || b.heldByBreaker > 0) {
                                <p-button label="Reset" icon="pi pi-refresh" size="small" severity="secondary" [outlined]="true"
                                          [disabled]="!canManage()" [loading]="resetting() === b.recipient" (onClick)="reset(b)"
                                          [pTooltip]="canManage() ? 'Close the breaker now and send the orders it is holding.' : 'Needs ' + manage"
                                          tooltipPosition="left" />
                            }
                        </div>
                        <dl class="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-sm">
                            <dt class="text-surface-500">Held orders</dt>
                            <dd [class.font-semibold]="b.heldByBreaker > 0" [class.text-orange-600]="b.heldByBreaker > 0">{{ b.heldByBreaker }}</dd>

                            <dt class="text-surface-500">Failures in a row</dt>
                            <dd>{{ b.consecutiveFailures }} <span class="text-surface-500">(opens at {{ s.breakerSettings.failureThreshold }} different orders)</span></dd>

                            @if (b.state !== 'Closed') {
                                <dt class="text-surface-500">Open since</dt>
                                <dd [attr.title]="b.openedAt | date: 'medium'">{{ since(b.openedAt) }}</dd>
                                <dt class="text-surface-500">Next try</dt>
                                <dd [attr.title]="b.nextProbeAt | date: 'medium'">{{ b.state === 'HalfOpen' ? 'probe out now' : until(b.nextProbeAt) }}</dd>
                            }

                            <dt class="text-surface-500">Last failure</dt>
                            <dd>
                                @if (b.lastFailureAt) {
                                    <span [attr.title]="b.lastFailureAt | date: 'medium'">{{ since(b.lastFailureAt) }}</span>
                                    @if (b.lastFailureReason) {
                                        <div class="text-xs text-surface-600 dark:text-surface-300 break-words" [attr.title]="b.lastFailureReason">{{ truncate(b.lastFailureReason, 200) }}</div>
                                    }
                                } @else { none }
                            </dd>

                            <dt class="text-surface-500">Last success</dt>
                            <dd [attr.title]="b.lastSuccessAt | date: 'medium'">{{ b.lastSuccessAt ? since(b.lastSuccessAt) : '—' }}</dd>

                            <dt class="text-surface-500">Times opened</dt>
                            <dd>{{ b.tripCount }}@if (b.changedBy) { <span class="text-surface-500"> · last reset by {{ b.changedBy }}</span> }</dd>
                        </dl>
                    </p-card>
                }
            </div>

            <!-- WAITING TO BE SENT with the method in force: a hand-off gave up and nothing is on its way to it. Sent
                 only when a person says so, each through the order's own Send to store / Send to XT lever. -->
            <div id="waiting-to-be-sent" class="mb-4">
            <p-card>
                <ng-template pTemplate="title">
                    <span>Waiting to be sent to {{ name(s.method) }}</span>
                    <p-tag class="ml-2" [value]="'' + (stalled()?.total ?? s.stalledCount)"
                           [severity]="(stalled()?.total ?? s.stalledCount) > 0 ? 'warn' : 'secondary'" />
                    <i class="pi pi-info-circle text-surface-400 ml-2 cursor-help"
                       pTooltip="The last 7 days, every location: orders whose hand-off failed, was dead-lettered or was retired, and that have nothing on its way to the method in force. Orders held by a breaker or waiting for a payment are not listed - they are sent by themselves."
                       tooltipPosition="top"></i>
                </ng-template>

                @if (stalled(); as w) {
                    @if (w.items.length > 0) {
                        <div class="flex flex-wrap items-center gap-3 mb-3">
                            <span class="text-sm text-surface-600 dark:text-surface-300">
                                {{ selectedCount() }} of {{ w.items.length }} selected
                                @if (w.truncated) { <span class="text-surface-500">· showing the newest {{ w.items.length }} of {{ w.total }}</span> }
                            </span>
                            <span class="flex-1"></span>
                            <p-button [label]="'Send ' + selectedCount() + ' selected to ' + name(w.method)" icon="pi pi-send" size="small"
                                      [disabled]="!canManage() || selectedCount() === 0 || sending()" [loading]="sending()"
                                      [pTooltip]="canManage() ? '' : 'Needs ' + manage" tooltipPosition="left"
                                      (onClick)="confirmOpen.set(true)" />
                        </div>
                    }
                    <p-table [value]="w.items" [paginator]="w.items.length > 25" [rows]="25" size="small" dataKey="orderKey">
                        <ng-template pTemplate="header">
                            <tr>
                                <th class="w-8">
                                    <input type="checkbox" aria-label="Select all" [checked]="allSelected()"
                                           [disabled]="!canManage()" (change)="toggleAll($any($event.target).checked)" />
                                </th>
                                <th>Order</th>
                                <th>Location</th>
                                <th>Created</th>
                                <th>What happened last</th>
                                <th>Last error</th>
                                <th>{{ name(w.method) }}</th>
                                <th>Sent now</th>
                            </tr>
                        </ng-template>
                        <ng-template pTemplate="body" let-i>
                            <tr>
                                <td>
                                    <input type="checkbox" [attr.aria-label]="'Select ' + i.orderId" [checked]="isSelected(i)"
                                           [disabled]="!canManage()" (change)="toggle(i, $any($event.target).checked)" />
                                </td>
                                <td class="whitespace-nowrap">
                                    <a class="text-primary hover:underline" routerLink="/orders"
                                       [queryParams]="{ locationId: i.locationId, search: i.orderId }">{{ i.orderId }}</a>
                                    @if (w.method === 'edge' && i.attemptedOnXt) {
                                        <p-tag class="ml-2" value="attempted on XT" severity="warn"
                                               pTooltip="XT was tried for this order. If XT created it before failing to answer, the store will get it as well." />
                                    }
                                </td>
                                <td class="text-sm whitespace-nowrap">
                                    <a class="text-primary hover:underline" [routerLink]="['/locations', i.locationId]">{{ i.locationId }}</a>
                                </td>
                                <td class="text-sm whitespace-nowrap" [attr.title]="i.createdAt | date: 'medium'">{{ since(i.createdAt) }}</td>
                                <td class="text-sm whitespace-nowrap">
                                    @if (i.lastAttempt; as a) {
                                        {{ name(a.recipient) }}
                                        <p-tag [value]="label(a.status)" [pTooltip]="tooltip(a.status)" [severity]="statusSeverity(a.status)" />
                                        <span class="text-surface-500">· {{ a.attempts }} {{ a.attempts === 1 ? 'attempt' : 'attempts' }}</span>
                                    } @else { — }
                                </td>
                                <td class="text-sm max-w-xs">
                                    <span class="block truncate" [attr.title]="i.lastAttempt?.lastError">{{ i.lastAttempt?.lastError ? truncate(i.lastAttempt.lastError, 120) : '—' }}</span>
                                </td>
                                <td class="text-sm whitespace-nowrap">
                                    @if (i.currentMethodStatus) {
                                        <p-tag [value]="label(i.currentMethodStatus)" [severity]="statusSeverity(i.currentMethodStatus)" />
                                    } @else { <span class="text-surface-500">never sent</span> }
                                </td>
                                <td class="text-sm max-w-xs">
                                    @if (outcomeFor(i); as r) {
                                        <p-tag [value]="outcomeLabel(r)" [severity]="outcomeSeverity(r)" [pTooltip]="r.message" />
                                    }
                                </td>
                            </tr>
                        </ng-template>
                        <ng-template pTemplate="emptymessage">
                            <tr><td colspan="8">Nothing is waiting: every order of the last {{ w.days }} days has a live hand-off to {{ name(w.method) }}.</td></tr>
                        </ng-template>
                    </p-table>
                    @if (w.excludedXtHasOrder > 0) {
                        <p class="text-sm text-surface-500 mt-2">
                            {{ w.excludedXtHasOrder }} more {{ w.excludedXtHasOrder === 1 ? 'is' : 'are' }} already registered in XT and {{ w.excludedXtHasOrder === 1 ? 'is' : 'are' }} not listed.
                        </p>
                    }
                    @if (w.excludedSentToStore > 0) {
                        <p class="text-sm text-surface-500 mt-2">
                            {{ w.excludedSentToStore }} more {{ w.excludedSentToStore === 1 ? 'is' : 'are' }} with the store Edge (in its queue
                            or already received) and {{ w.excludedSentToStore === 1 ? 'is' : 'are' }} not listed: Send to XT refuses those,
                            because they would reach two tills.
                        </p>
                    }
                    @if (orphanResults().length > 0) {
                        <!-- What the last send refused for orders that have since left the list. -->
                        <div class="mt-3 text-sm">
                            <p class="font-medium mb-1">Not sent by the last send</p>
                            <ul class="list-disc pl-5">
                                @for (r of orphanResults(); track r.locationId + '|' + r.orderId) {
                                    <li>
                                        <a class="text-primary hover:underline" routerLink="/orders"
                                           [queryParams]="{ locationId: r.locationId, search: r.orderId }">{{ r.orderId }}</a>
                                        <span class="text-surface-500"> ({{ r.locationId }})</span> — {{ outcomeLabel(r) }}: {{ r.message }}
                                    </li>
                                }
                            </ul>
                        </div>
                    }
                } @else {
                    <p class="text-sm text-surface-500">Loading…</p>
                }
            </p-card>
            </div>

            <p-card>
                <ng-template pTemplate="title">
                    <span>Hand-offs not getting through</span>
                    <i class="pi pi-info-circle text-surface-400 ml-2 cursor-help"
                       pTooltip="The last 24 hours, every location: failed (the bus is retrying), gave up (dead-lettered: re-send by hand from the order), or held by a breaker (sent automatically when it closes)."
                       tooltipPosition="top"></i>
                </ng-template>
                <p-table [value]="s.recentFailures" [paginator]="s.recentFailures.length > 15" [rows]="15" size="small">
                    <ng-template pTemplate="header">
                        <tr>
                            <th>Order</th>
                            <th>Location</th>
                            <th>To</th>
                            <th>Status</th>
                            <th class="text-right">Attempts</th>
                            <th>Last error</th>
                            <th>When</th>
                        </tr>
                    </ng-template>
                    <ng-template pTemplate="body" let-f>
                        <tr>
                            <td class="whitespace-nowrap">
                                <a class="text-primary hover:underline" routerLink="/orders"
                                   [queryParams]="{ locationId: f.locationId, search: f.orderId }">{{ f.orderId }}</a>
                            </td>
                            <td class="text-sm whitespace-nowrap">
                                <a class="text-primary hover:underline" [routerLink]="['/locations', f.locationId]">{{ f.locationId }}</a>
                            </td>
                            <td class="text-sm whitespace-nowrap">{{ name(f.recipient) }}</td>
                            <td><p-tag [value]="label(f.status)" [pTooltip]="tooltip(f.status)" [severity]="failureSeverity(f)" /></td>
                            <td class="text-right">{{ f.attempts }}</td>
                            <td class="text-sm max-w-md">
                                <span class="block truncate" [attr.title]="f.lastError">{{ f.lastError ?? '—' }}</span>
                            </td>
                            <td class="text-sm whitespace-nowrap" [attr.title]="f.lastModified | date: 'medium'">{{ since(f.lastModified) }}</td>
                        </tr>
                    </ng-template>
                    <ng-template pTemplate="emptymessage">
                        <tr><td colspan="7">Every hand-off in the last 24 hours got through.</td></tr>
                    </ng-template>
                </p-table>
            </p-card>
        }

        <p-dialog [header]="'Send to ' + name(stalled()?.method)" [visible]="confirmOpen()" (visibleChange)="confirmOpen.set($event)"
                  [modal]="true" [style]="{ width: '34rem' }" [breakpoints]="{ '640px': '95vw' }">
            <p class="text-sm mb-3">
                Send <b>{{ selectedCount() }}</b> {{ selectedCount() === 1 ? 'order' : 'orders' }} to {{ name(stalled()?.method) }}?
                Each goes through its own
                {{ stalled()?.method === 'xt' ? 'Send to XT' : 'Send to store' }} lever, with that lever's checks: an order it
                refuses is left as it is, and the others still go.
            </p>
            @if (stalled()?.method === 'edge' && selectedAttemptedOnXt() > 0) {
                <p-message severity="warn" styleClass="mb-3">
                    XT was attempted for {{ selectedAttemptedOnXt() }} of these. If XT created them before failing to answer, the store will get them as well.
                </p-message>
            }
            <ng-template pTemplate="footer">
                <p-button label="Cancel" severity="secondary" [text]="true" [disabled]="sending()" (onClick)="confirmOpen.set(false)" />
                <p-button [label]="'Send ' + selectedCount()" icon="pi pi-send" [loading]="sending()" (onClick)="send()" />
            </ng-template>
        </p-dialog>
    `
})
export class OrderHandoffPage implements OnInit, OnDestroy {
    private readonly api = inject(MiddlewareApi);
    private readonly perms = inject(PermissionsService);

    readonly manage = MANAGE;
    readonly methods = [
        { label: 'Store Edge', value: 'edge' },
        { label: 'XT POS (legacy)', value: 'xt' }
    ];

    readonly state = signal<OrderHandoff | null>(null);
    readonly error = signal<string | null>(null);
    readonly note = signal<string | null>(null);

    /** What the operator has picked; differs from the state's method only while a change is being made. */
    readonly choice = signal<string | null>(null);
    readonly reason = signal<string | null>(null);
    readonly applying = signal(false);
    readonly resetting = signal<string | null>(null);

    /** The orders waiting to be sent with the method in force, and which of them the operator has ticked (by orderKey). */
    readonly stalled = signal<StalledHandoffs | null>(null);
    readonly selected = signal<ReadonlySet<string>>(new Set());
    /** The last bulk send's answer per order, kept across reloads so a refusal stays readable. */
    readonly results = signal<ReadonlyMap<string, StalledSendResult>>(new Map());
    readonly sending = signal(false);
    readonly confirmOpen = signal(false);
    /** The banner after a switch: how many were waiting, the method they were waiting on, the one just chosen. */
    readonly offer = signal<{ count: number; from: string; to: string } | null>(null);
    /** Set by "Review and send" and by a switch, so the next list read ticks every row. */
    private preselect = false;

    readonly selectedCount = computed(() => this.selectedItems().length);
    readonly allSelected = computed(() => {
        const items = this.stalled()?.items ?? [];
        return items.length > 0 && items.every(i => this.selected().has(i.orderKey));
    });
    readonly selectedAttemptedOnXt = computed(() => this.selectedItems().filter(i => i.attemptedOnXt).length);
    /** Answers for orders no longer in the list: what was refused (or failed) and has since dropped out of view. */
    readonly orphanResults = computed(() => {
        const listed = new Set((this.stalled()?.items ?? []).map(i => resultKey(i.locationId, i.orderId)));
        return [...this.results().entries()]
            .filter(([key, r]) => !listed.has(key) && r.outcome !== 'queued' && r.outcome !== 'alreadyRegistered')
            .map(([, r]) => r);
    });
    private readonly selectedItems = computed(() =>
        (this.stalled()?.items ?? []).filter(i => this.selected().has(i.orderKey)));

    readonly canManage = computed(() => this.perms.has(MANAGE));
    readonly changing = computed(() => {
        const s = this.state();
        return !!s && !!this.choice() && this.choice() !== s.method;
    });

    readonly since = relativeTime;
    readonly label = orderLabel;
    readonly tooltip = orderTooltip;

    private timer: ReturnType<typeof setInterval> | null = null;
    private loadSub?: Subscription;
    private stalledSub?: Subscription;

    ngOnInit(): void {
        this.load();
        this.timer = setInterval(() => this.load(), REFRESH_MS);
    }

    ngOnDestroy(): void {
        if (this.timer !== null) { clearInterval(this.timer); this.timer = null; }
        this.loadSub?.unsubscribe();
        this.stalledSub?.unsubscribe();
    }

    name(recipient: string | null | undefined): string {
        const key = (recipient ?? '').toLowerCase();
        return RECIPIENT_NAMES[key] ?? recipient ?? '—';
    }

    /** A switch moved. On means "send to this one"; the live one turned off means "send to the other one" - there is
     *  no state with nothing receiving orders, so a switch is never left off on its own. Either way the dialog asks
     *  why before anything changes. */
    switchMethod(method: string, checked: boolean): void {
        if (!this.canManage() || this.applying()) return;
        const target = checked ? method : (this.methods.find(m => m.value !== method)?.value ?? method);
        this.choice.set(target);
    }

    cancelChange(): void {
        this.choice.set(this.state()?.method ?? null);
        this.reason.set(null);
    }

    apply(): void {
        const method = this.choice();
        const reason = this.reason()?.trim();
        if (!method || !reason) return;
        const previous = this.state()?.method ?? null;
        this.applying.set(true);
        this.error.set(null);
        this.api.setOrderHandoff(method, reason).subscribe({
            next: (s) => {
                this.applying.set(false);
                this.reason.set(null);
                this.accept(s, true);
                this.note.set(`Online orders now go to the ${this.name(s.method)}. Every instance picks this up within a few seconds.`);
                // THE OFFER: the answer counts what is waiting for the method just chosen. Nothing is sent until a
                // person has looked at the list and said so.
                if (previous && previous !== s.method && s.stalledCount > 0) {
                    this.offer.set({ count: s.stalledCount, from: previous, to: s.method });
                } else {
                    this.offer.set(null);
                }
                // The list belongs to the old method: drop it and its ticks, and read the new one now.
                this.stalled.set(null);
                this.selected.set(new Set());
                this.results.set(new Map());
                this.loadStalled(s.method);
            },
            error: (e) => { this.applying.set(false); this.error.set(this.describe(e)); }
        });
    }

    reset(b: OrderHandoffBreaker): void {
        this.resetting.set(b.recipient);
        this.error.set(null);
        this.api.resetHandoffBreaker(b.recipient).subscribe({
            next: (s) => {
                this.resetting.set(null);
                this.accept(s, false);
                this.note.set(`The ${this.name(b.recipient)} breaker is closed; the ${b.heldByBreaker} held order(s) are being sent.`);
            },
            error: (e) => { this.resetting.set(null); this.error.set(this.describe(e)); }
        });
    }

    /** "Review and send": every listed row ticked, and the list brought into view. The banner goes; the list stays. */
    review(): void {
        this.offer.set(null);
        const items = this.stalled()?.items;
        if (items) this.selected.set(new Set(items.map(i => i.orderKey)));
        else this.preselect = true; // the list is still loading: tick it when it lands
        document.getElementById('waiting-to-be-sent')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    isSelected(i: StalledHandoff): boolean {
        return this.selected().has(i.orderKey);
    }

    toggle(i: StalledHandoff, on: boolean): void {
        const next = new Set(this.selected());
        if (on) next.add(i.orderKey); else next.delete(i.orderKey);
        this.selected.set(next);
    }

    toggleAll(on: boolean): void {
        this.selected.set(on ? new Set((this.stalled()?.items ?? []).map(i => i.orderKey)) : new Set());
    }

    send(): void {
        const w = this.stalled();
        const items = this.selectedItems();
        if (!w || items.length === 0) return;
        this.sending.set(true);
        this.error.set(null);
        this.api.sendStalledHandoffs(w.method, items.map(i => ({ locationId: i.locationId, orderId: i.orderId, source: i.source })))
            .subscribe({
                next: (r) => {
                    this.sending.set(false);
                    this.confirmOpen.set(false);
                    this.results.set(new Map(r.results.map(x => [resultKey(x.locationId, x.orderId), x] as const)));
                    // What went is no longer waiting; what was refused stays ticked-off but listed, with the reason.
                    this.selected.set(new Set());
                    const registered = r.results.filter(x => x.outcome === 'alreadyRegistered').length;
                    this.note.set(
                        `${r.queued} queued for the ${this.name(r.method)}`
                        + (registered > 0 ? `, ${registered} already in XT` : '')
                        + (r.refused > 0 ? `, ${r.refused} refused` : '')
                        + (r.failed > 0 ? `, ${r.failed} failed` : '')
                        + '. Each order\'s own screen has the details.');
                    this.load();
                },
                error: (e) => { this.sending.set(false); this.error.set(this.describe(e)); }
            });
    }

    outcomeFor(i: StalledHandoff): StalledSendResult | undefined {
        return this.results().get(resultKey(i.locationId, i.orderId));
    }

    outcomeLabel(r: StalledSendResult): string {
        switch (r.outcome) {
            case 'queued': return 'queued';
            case 'alreadyRegistered': return 'already registered';
            case 'notFound': return 'not found';
            case 'failed': return 'failed';
            default: return 'refused';
        }
    }

    outcomeSeverity(r: StalledSendResult): 'success' | 'info' | 'warn' | 'danger' {
        switch (r.outcome) {
            case 'queued': return 'success';
            case 'alreadyRegistered': return 'info';
            case 'failed': return 'danger';
            default: return 'warn';
        }
    }

    statusSeverity(status: string | null | undefined): 'warn' | 'danger' | 'secondary' | 'info' {
        const v = (status ?? '').toLowerCase();
        if (v.includes('held')) return 'warn';
        if (v.includes('fail') || v.includes('dead')) return 'danger';
        if (v.includes('abandon')) return 'secondary';
        return 'info';
    }

    stateLabel(b: OrderHandoffBreaker): string {
        switch (b.state) {
            case 'Closed': return 'closed';
            case 'HalfOpen': return 'probing';
            case 'Open': return `open since ${this.clock(b.openedAt)}`;
            default: return b.state.toLowerCase();
        }
    }

    stateSeverity(b: OrderHandoffBreaker): 'success' | 'warn' | 'danger' | 'secondary' {
        switch (b.state) {
            case 'Closed': return 'success';
            case 'HalfOpen': return 'warn';
            case 'Open': return 'danger';
            default: return 'secondary';
        }
    }

    stateTitle(b: OrderHandoffBreaker): string {
        switch (b.state) {
            case 'Closed': return 'Hand-offs go through.';
            case 'HalfOpen': return 'One held order has been sent to test the point of sale; the rest wait for its answer.';
            case 'Open': return 'New hand-offs are held, not sent. One is sent as a probe at the next try; if it gets through, the rest follow.';
            default: return b.state;
        }
    }

    failureSeverity(f: OrderHandoffFailure): 'warn' | 'danger' | 'secondary' {
        const v = (f.status ?? '').toLowerCase();
        if (v.includes('held')) return 'warn';
        if (v.includes('fail') || v.includes('dead')) return 'danger';
        return 'secondary';
    }

    until(iso: string | null | undefined): string {
        if (!iso) return '—';
        const seconds = Math.round((new Date(iso).getTime() - Date.now()) / 1000);
        if (Number.isNaN(seconds)) return '—';
        if (seconds <= 0) return 'due now';
        if (seconds < 60) return `in ${seconds}s`;
        return `in ${Math.round(seconds / 60)} min`;
    }

    truncate(text: string, max: number): string {
        return text.length <= max ? text : text.slice(0, max - 1) + '…';
    }

    private clock(iso: string | null | undefined): string {
        if (!iso) return '?';
        const d = new Date(iso);
        return Number.isNaN(d.getTime()) ? '?' : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }

    private load(): void {
        // A tick that lands while the previous one is in flight replaces it.
        this.loadSub?.unsubscribe();
        this.loadSub = this.api.getOrderHandoff().subscribe({
            next: (s) => { this.accept(s, false); this.error.set(null); this.loadStalled(s.method); },
            error: (e) => this.error.set(this.describe(e))
        });
    }

    /** The list for the method in force. Ticks survive a refresh for the rows still listed; the rest are dropped. */
    private loadStalled(method: string): void {
        this.stalledSub?.unsubscribe();
        this.stalledSub = this.api.getStalledHandoffs(method).subscribe({
            next: (w) => {
                if (this.state()?.method !== w.method) return; // an answer for a method that is no longer in force
                this.stalled.set(w);
                const listed = new Set(w.items.map(i => i.orderKey));
                if (this.preselect) {
                    this.preselect = false;
                    this.selected.set(listed);
                } else {
                    this.selected.set(new Set([...this.selected()].filter(k => listed.has(k))));
                }
            },
            error: (e) => this.error.set(this.describe(e))
        });
    }

    /** Takes a fresh read. The operator's pending choice survives a background tick; an apply resets it. */
    private accept(s: OrderHandoff, applied: boolean): void {
        const pending = this.changing();
        this.state.set(s);
        if (applied || !pending) this.choice.set(s.method);
    }

    private describe(e: any): string {
        if (e?.status === 0) return 'Cannot reach the API.';
        if (e?.error?.error) return e.error.error;
        if (e?.error?.detail) return e.error.detail;
        if (e?.status === 401) return 'Unauthorized — signing in…';
        if (e?.status === 403) return `Forbidden — your role lacks the permission this needs (orders:read to view, ${MANAGE} to change).`;
        return `Error ${e?.status ?? ''}: ${e?.message ?? 'request failed'}`;
    }
}
